import React from 'react';

import {
  QueryClient,
  NetworkMode,
  QueryClientProvider,
} from '@tanstack/react-query';
import { createBrowserHistory, History } from 'history';
import { Router } from 'react-router';
import { NotificationBannerProvider } from 'signer-app/parts/notifications/notification-banners';
import { IntlShape, RawIntlProvider } from 'react-intl';
import {
  FeatureFlagProvider as SignerFeatureFlagProvider,
  SignerAppFeatureFlags,
  FeatureFlagProvider,
} from 'signer-app/context/feature-flags';
import {
  SignerAppClient,
  SignerAppClientProvider,
} from 'signer-app/context/signer-app-client';
import { AriaLiveContextProvider } from 'signer-app/utils/use-aria-live';
import TouchProvider from 'signer-app/parts/touch-provider';
import {
  useHydrateBundleProps,
  useUpdateWhiteLabelingBundleProps,
} from 'signer-app/bundle-props';
import { WhiteLabelStyles } from 'signer-app/white-labeling/types';
import ErrorBoundary from 'signer-app/parts/error-boundary';

export type SignerAppProps = React.PropsWithChildren<{
  suspenseFallback?: React.ReactNode;
  featureFlags: SignerAppFeatureFlags;
  signerAppClient: SignerAppClient;
  history?: History;
  intl: IntlShape;
  whiteLabelStyles: WhiteLabelStyles;
  hasCustomStyles: boolean;
  customLogoUri: string | undefined;
  signerDbxAccountId?: string;
  signerAccountId?: string;
  tsmGuid: string;
  embeddedData: {
    parentUrl: string;
    redirectUrl: string;
    isEmbeddedSigning: boolean;
  };
  signers?: {
    key: string;
    name: string;
    email: string;
    tsmGuid: string;
  }[];
}>;
const defaultFallback = <div />;

export default function SignerApp({
  children,
  featureFlags,
  suspenseFallback = defaultFallback,
  signerAppClient,
  history: maybeHistory,
  intl,
  whiteLabelStyles,
  hasCustomStyles,
  customLogoUri,
  signerAccountId,
  signerDbxAccountId,
  tsmGuid,
  embeddedData,
  signers,
}: SignerAppProps) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        // Although networkMode is a great idea in theory, headless selenium tests trick react-query
        // into thinking there is no network connection and all queries are 'paused'
        // https://tanstack.com/query/v4/docs/react/guides/network-mode
        defaultOptions: {
          queries: {
            cacheTime: Infinity,
            staleTime: Infinity,
            networkMode: 'always' as NetworkMode,
            retry: false, // don't retry on failure
            refetchOnWindowFocus: false, // don't refetch when a window/tab is refocused
            refetchOnReconnect: false, // don't refetch if the user regains internet connection
          },
        },
      }),
  );
  const history = React.useMemo(() => {
    if (maybeHistory) {
      return maybeHistory;
    }
    return createBrowserHistory();
  }, [maybeHistory]);
  useHydrateBundleProps({
    whiteLabelStyles,
    hasCustomStyles,
    customLogoUri,
    signerAccountId,
    signerDbxAccountId,
    tsmGuid,
    embeddedData,
    signers,
  });

  // Ensure that whiteLabelStyles and customLogoUri are kept up to date for
  // the purpose of dynamic updates served from the white-labeling editor.
  useUpdateWhiteLabelingBundleProps(
    { whiteLabelStyles, customLogoUri },
    { enabled: hasCustomStyles },
  );

  return (
    <React.Suspense fallback={suspenseFallback}>
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <SignerAppClientProvider client={signerAppClient}>
            <NotificationBannerProvider>
              <TouchProvider>
                <RawIntlProvider value={intl}>
                  <ErrorBoundary>
                    <AriaLiveContextProvider>
                      <FeatureFlagProvider featureFlags={featureFlags || {}}>
                        <SignerFeatureFlagProvider
                          featureFlags={featureFlags || {}}
                        >
                          {children}
                        </SignerFeatureFlagProvider>
                      </FeatureFlagProvider>
                    </AriaLiveContextProvider>
                  </ErrorBoundary>
                </RawIntlProvider>
              </TouchProvider>
            </NotificationBannerProvider>
          </SignerAppClientProvider>
        </Router>
      </QueryClientProvider>
    </React.Suspense>
  );
}
